import { Box, Typography, Switch, TextField, ToggleButton, ToggleButtonGroup, InputAdornment } from "@mui/material";
import { colors } from "../../ui/colors";
import { useState, useEffect, useMemo, type WheelEvent } from "react";
import { SASSwitchStyles } from "../../ui/styles/inputStyles";
import { useForm, type Resolver } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuoteTotal } from "./basic-quote/hooks/useQuoteTotal";
import { QuoteModeEnum } from "./enums/quote-create.enums";
import { useDispatch, useSelector } from "react-redux";
import { quoteCreateActions } from "../../../redux/features/quotes/quote-create-advanced";
import { isDepositRequirementSatisfied } from "./helper/quoteServicePricingGate";
import { PaymentMethodSelector } from "../../invoice/create/PaymentMethods";
import { useCompanyPaymentsStatusQuery } from "../../invoice/create/hooks/usePayments";
import { RootState } from "../../../redux/store";
import type { QuoteCreateState } from "../../../redux/features/quotes/quote-create-advanced/types";

type DepositFormValues = {
  depositAmount?: number;
  depositPercent?: number;
};

/** RHF `valueAsNumber: true` turns empty input into NaN; Zod `z.number()` rejects NaN before refinements. */
const optionalNumberFromNumericInput = () =>
  z.preprocess((val) => {
    if (val === "" || val === null || val === undefined) return undefined;
    if (typeof val === "number" && Number.isNaN(val)) return undefined;
    return val;
  }, z.number().optional());

export const QuoteDeposit = ({ mode }: { mode: QuoteModeEnum }) => {
  const [depositMode, setDepositMode] = useState<"amount" | "percent">("amount");
  const dispatch = useDispatch();
  const { setDepositAmount, setDepositPercent, setQuote } = quoteCreateActions;
  const quote = useSelector((state: RootState) => (state.quoteCreate as QuoteCreateState).quote);
  const depositRequired = Boolean(quote?.depositRequired);
  const depositRequirementMet = isDepositRequirementSatisfied(quote);
  const total = useQuoteTotal(mode);
  const company = useSelector((state: RootState) => state.userState.company);
  const { data: paymentAvailableData } = useCompanyPaymentsStatusQuery(company?.id);

  const depositSchema = useMemo(
    () =>
      z
        .object({
          depositAmount: optionalNumberFromNumericInput(),
          depositPercent: optionalNumberFromNumericInput(),
        })
        .superRefine((data, ctx) => {
          if (depositMode === "amount") {
            const v = data.depositAmount;
            if (v == null || Number.isNaN(v)) return;
            if (v < 0) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Deposit must be at least 0", path: ["depositAmount"] });
            if (v > total) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Deposit cannot exceed total ($${total})`, path: ["depositAmount"] });
            return;
          }
          const p = data.depositPercent;
          if (p == null || Number.isNaN(p)) return;
          if (p < 0 || p > 100) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a percentage between 0 and 100", path: ["depositPercent"] });
          }
          const dollars = Math.round(((total * p) / 100) * 100) / 100;
          if (dollars > total) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Deposit cannot exceed total ($${total})`, path: ["depositPercent"] });
          }
        }),
    [depositMode, total],
  );

  const {
    register,
    handleSubmit,
    trigger,
    reset,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<DepositFormValues>({
    resolver: zodResolver(depositSchema) as Resolver<DepositFormValues>,
    mode: "onChange",
    defaultValues: {
      depositAmount: undefined,
      depositPercent: undefined,
    },
  });

  const watchPercent = watch("depositPercent");

  useEffect(() => {
    clearErrors();
  }, [depositMode, clearErrors]);

  const depositRegister = register("depositAmount", { valueAsNumber: true });
  const percentRegister = register("depositPercent", { valueAsNumber: true });

  const onSubmit = (data: DepositFormValues) => {
    if (depositMode === "percent") {
      const p = typeof data.depositPercent === "number" && !Number.isNaN(data.depositPercent) ? data.depositPercent : null;
      if (p != null && p > 0) {
        const dollars = Math.round(((total * p) / 100) * 100) / 100;
        dispatch(setDepositPercent(p));
        dispatch(setDepositAmount(dollars));
        return;
      }
      dispatch(setDepositPercent(null));
      dispatch(setDepositAmount(0));
      return;
    }
    if (typeof data.depositAmount === "number" && !Number.isNaN(data.depositAmount)) {
      dispatch(setDepositPercent(null));
      dispatch(setDepositAmount(data.depositAmount));
      return;
    }

    dispatch(setDepositPercent(null));
    dispatch(setDepositAmount(0));
  };

  useEffect(() => {
    void trigger("depositAmount");
    void trigger("depositPercent");
  }, [total, trigger, depositMode]);

  // Editing a quote that already has a deposit: show the switch on and keep flag in sync.
  useEffect(() => {
    if (!quote || quote.depositRequired) return;
    const hasAmount = Number(quote.depositAmount ?? 0) > 0;
    const hasPercent = quote.depositPercent != null && Number(quote.depositPercent) > 0;
    if (hasAmount || hasPercent) {
      dispatch(setQuote({ depositRequired: true }));
    }
  }, [quote, dispatch, setQuote]);

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} justifyContent="flex-start">
        <Typography variant="body1" fontWeight={700} color={colors.gray[900]}>
          Require a deposit to accept the quote:
        </Typography>

        <Switch
          sx={{ ...SASSwitchStyles }}
          checked={depositRequired}
          onChange={(e) => {
            const checked = e.target.checked;
            if (!checked) {
              reset({ depositAmount: undefined, depositPercent: undefined });
              dispatch(setDepositAmount(0));
              dispatch(setDepositPercent(null));
              dispatch(setQuote({ depositRequired: false }));
              return;
            }
            dispatch(setQuote({ depositRequired: true }));
          }}
        />
      </Box>

      {depositRequired && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={depositMode}
            onChange={(_, v) => {
              if (!v) return;

              const currentValue =
                depositMode === "amount"
                  ? watch("depositAmount")
                  : watch("depositPercent");

              setDepositMode(v as "amount" | "percent");

              if (v === "amount") {
                setValue("depositPercent", undefined);
                if (typeof currentValue === "number" && !Number.isNaN(currentValue)) {
                  setValue("depositAmount", currentValue, { shouldValidate: true, shouldDirty: true });
                  dispatch(setDepositPercent(null));
                  dispatch(setDepositAmount(currentValue));
                } else {
                  dispatch(setDepositPercent(null));
                  dispatch(setDepositAmount(0));
                }
              } else {
                setValue("depositAmount", undefined);
                if (typeof currentValue === "number" && !Number.isNaN(currentValue)) {
                  setValue("depositPercent", currentValue, { shouldValidate: true, shouldDirty: true });
                  const dollars = Math.round(((total * currentValue) / 100) * 100) / 100;
                  dispatch(setDepositPercent(currentValue));
                  dispatch(setDepositAmount(dollars));
                } else {
                  dispatch(setDepositPercent(null));
                  dispatch(setDepositAmount(0));
                }
              }

              clearErrors(["depositAmount", "depositPercent"]);
            }}
            sx={{ alignSelf: "flex-start" }}
          >
            <ToggleButton value="amount">Fixed ($)</ToggleButton>
            <ToggleButton value="percent">Percent (%)</ToggleButton>
          </ToggleButtonGroup>

          <Box
            display="flex"
            justifyContent="space-between"
            sx={{ mt: 1, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}
          >
            <Typography color={colors.gray[600]}>
              {depositMode === "amount" ? "Deposit amount" : "Deposit (% of quote total)"}
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              {depositMode === "amount" ? (
                <TextField
                  {...depositRegister}
                  label="Deposit ($)"
                  key="depositAmount"
                  type="number"
                  fullWidth
                  size="small"
                  error={!!errors.depositAmount || (depositRequired && !depositRequirementMet)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{
                    style: { textAlign: "right", fontWeight: 600 },
                    onWheel: (e: WheelEvent<HTMLInputElement>) => {
                      e.currentTarget.blur();
                      e.stopPropagation();
                      e.preventDefault();
                    },
                  }}
                  helperText={
                    errors.depositAmount?.message ||
                    (depositRequired && !depositRequirementMet
                      ? "Enter a deposit greater than zero to generate the quote."
                      : undefined)
                  }
                  onChange={async (e) => {
                    const rawValue = e.target.value;

                    if (rawValue === "") {
                      reset({ depositAmount: undefined, depositPercent: undefined });
                      dispatch(setDepositAmount(0));
                      dispatch(setDepositPercent(null));
                      clearErrors("depositAmount");
                      return;
                    }

                    depositRegister.onChange(e);

                    const numericValue = Number(rawValue);

                    setValue("depositAmount", numericValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });

                    const isValid = await trigger("depositAmount");

                    if (isValid) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                />
              ) : (
                <TextField
                  {...percentRegister}
                  label="Deposit (%)"
                  key="depositPercent"
                  type="number"
                  fullWidth
                  size="small"
                  error={!!errors.depositPercent || (depositRequired && !depositRequirementMet)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0,
                    max: 100,
                    style: { textAlign: "right", fontWeight: 600 },
                    onWheel: (e: WheelEvent<HTMLInputElement>) => {
                      e.currentTarget.blur();
                      e.stopPropagation();
                      e.preventDefault();
                    },
                  }}
                  helperText={
                    errors.depositPercent?.message ||
                    (depositRequired && !depositRequirementMet
                      ? "Enter a percentage greater than zero to generate the quote."
                      : typeof watchPercent === "number" && !Number.isNaN(watchPercent) && watchPercent > 0
                        ? `≈ $${(Math.round(((total) / 100) * watchPercent)).toFixed(2)}`
                        : " ")
                  }
                  onChange={async (e) => {
                    const rawValue = e.target.value;
                    if (rawValue === "") {
                      reset({ depositAmount: undefined, depositPercent: undefined });
                      dispatch(setDepositAmount(0));
                      dispatch(setDepositPercent(null));
                      clearErrors("depositPercent");
                      return;
                    }
                    percentRegister.onChange(e);
                    const numericValue = Number(rawValue);
                    setValue("depositPercent", numericValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    const isValid = await trigger("depositPercent");
                    if (isValid) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                />
              )}
            </form>
          </Box>

          <PaymentMethodSelector availablePayments={paymentAvailableData} />
        </Box>
      )}
    </>
  );
};
